/**
 * Scribe
 *
 * This module provides a client-side api to collect data with Scribe.
 *
 * @module    scribe
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
/* eslint-env browser */
import $ from 'jquery';
import _ from 'underscore';
import SCRIBE_EVENTS from './scribe_event_constants';
import ScribeStatsTracker from './scribe_stats_tracker';
import ScribeClient from './scribe_client';
import ScribeHeartbeat from './scribe_heartbeat';
import ScribePageVisibilityTracker from './scribe_page_visibility_tracker';
import ScribeClickTracker from './scribe_click_tracker';

const WSP_SANDBOX_IMPRESSION = 'SponsoredProductsSandboxImpression';
const SECONDS_PER_DAY = 86400; // a day in seconds

const _unflattenRfCstmVars = (rfCstmVars = '') => {
  return rfCstmVars.split(';').reduce((acc, keyValuePair) => {
    const [key, value] = keyValuePair.split('=');
    acc[key] = value;
    return acc;
  }, {});
};

/**
 * Construct Scribe client
 *
 * (Note: This module returns a singleton instance)
 *
 * @class
 */
class Scribe {
  constructor(config) {
    this.config = config;

    this.customVarsExcludeList = [
      '_Engine',
      'MkcId',
      'PageType',
      'b2bLevel',
      'CuID',
      'EventType',
      'customer_zip_code',
      'CSNADMINID',
      'libra_g',
      'libra_d',
      'libra_c',
      'libra_f',
      '_wf_osm_shown',
      '_wf_osm_cu_seg',
      'LoginStatusFlag',
      'deviceWidth',
      'deviceHeight',
      'isHeaderFooterResponsive',
      'ShipsInTime',
      'lang',
    ];

    this.customVarAllowList = ['payment_method_id'];
    this.statsTracker = new ScribeStatsTracker();
    this.client = new ScribeClient(this.config, this.statsTracker);
    this.pageVisibilityTracker = new ScribePageVisibilityTracker(this.client);
    this.clickTracker = new ScribeClickTracker(this.config, this.client);

    // expose method for 3rd party action
    if (typeof window !== 'undefined') {
      window.__trackThirdPartyEvent = this.trackThirdPartyAction.bind(this);
    }
  }

  /**
   * Send a Scribe event.
   *
   * Event data will be sent with a timestamp, transaction id, and store id.
   *
   * @param {Object} eventData Scribe Event Data
   * @param {String} eventData.eventType Type of scribe event (required)
   * @return {Promise} Send event promise
   */
  sendEvent(eventData) {
    return this.client.sendEvent(eventData);
  }

  /**
   * Enable click tracking.  Tracks click stream on elements with a click
   * location data attribute using delegate event bindings.
   */
  trackBodyClicks() {
    this.clickTracker.trackBodyClicks();
  }

  filterCustomVars(customVars) {
    return _.omit(customVars, this.customVarsExcludeList);
  }

  extractCustomVars(customVars) {
    return _.pick(customVars, this.customVarAllowList);
  }

  /**
   * Tracks the viewing of a page by dispatching a PageView.
   */
  // eslint-disable-next-line max-params
  trackPageViewing({
    isAdBlockerEnabled,
    isPrivateBrowsing,
    canvasHashes,
    fontHash,
    pluginHash,
    languageHash,
    isSPA,
    marketingCategory,
    pageType,
    b2bLevelName,
    datacenter,
    serviceID,
    transactionID,
  }) {
    // iAB is a boolean field which shows whether the adblock was enabled or disabled when this pageView occurred
    const pageViewData = {
      eventType: SCRIBE_EVENTS.PAGE_VIEW,
      iAB: isAdBlockerEnabled,
      iPB: isPrivateBrowsing,
      cHashes: this.encodeScribeMap(canvasHashes),
      fHash: fontHash,
      pHash: pluginHash,
      lHash: languageHash,
      isSPA,
      referrer: document.referrer,
      marketingCategoryId: marketingCategory,
      pageType,
      deviceWidth: window.innerWidth,
      deviceHeight: window.innerHeight,
      b2bLevelName,
      rdc: datacenter,
      serviceID,
      transactionID,
    };
    this.client.sendEvent(pageViewData);
  }

  /**
   * Initializes the Heartbeat
   *
   * The Heartbeat will 'pulse' at an interval and send an event back to
   * Scribe.  Calling this method successive times will stop any running
   * heartbeat and start new one.
   *
   * @return {Promise} Heartbeat Event progress promise
   */
  initializeHeartbeat() {
    return this._setupHeartbeat();
  }

  /**
   * Track when the page has been prerendered
   */
  trackPrerendering() {
    this.pageVisibilityTracker.trackPrerendering();
  }

  /**
   * Track when page visibility changes
   */
  trackVisibilityChanges() {
    this.pageVisibilityTracker.trackVisibilityChanges();
  }

  sendBatchedImpressionEvents(event) {
    return this.client.sendBatchedEvents(event);
  }

  sendBatchedBillingPaymentOptionsEvents(events) {
    return this.client.sendBatchedEvents(events);
  }

  /**
   * Track impressions
   * @param {Object} event event data
   */
  trackImpressions(event) {
    event.eventType = SCRIBE_EVENTS.IMPRESSION;
    this.client.sendEvent(event, true);
  }

  /**
   * Track Pricing shown to customer
   *
   * @param {Object} event scribe data
   */
  trackPricing(event) {
    this.client.sendEvent(event);
  }

  /**
   * Track optionId(s) selected by our customers
   *
   * @param {Object} event scribe data
   */
  trackSelectOption(event) {
    event.eventType = SCRIBE_EVENTS.SELECT_OPTION;
    this.client.sendEvent(event);
  }

  /**
   * adds the base properties that every SiteImpressionEvent should have
   *
   * @param {Object} impressionData the current SiteImpressionEvent data
   *
   * @returns {Object} modified event data that can be sent to Scribe
   */
  createSiteImpressionEvent(impressionData) {
    impressionData.eventType = SCRIBE_EVENTS.SITE_IMPRESSION;
    impressionData.impressionTimestamp = new Date().getTime();
    impressionData.referringTransactionId = this.config.transactionID;
    return impressionData;
  }

  /**
   * extracts SponsoredProductImpressionEvents from browseObjects to be used with sendBatchedImpressionEvents
   *
   * @param {Array} browseObjects an array of browseObjects
   * @param {Integer} categoryId the id of the browse category
   * @param {String} parentSku the SKU of the product whose page this sponsored product was shown on (if applicable)
   *
   * @return {Object} a SponsoredProductImpressionEvent
   */
  createSponsoredProductImpressionEvents(
    browseObjects,
    categoryId,
    parentSku = null
  ) {
    if (browseObjects) {
      return browseObjects
        .filter(
          browseObject =>
            browseObject.sku &&
            (browseObject.is_featured || browseObject.isFeatured)
        )
        .map(browseObject => ({
          eventType: SCRIBE_EVENTS.SPONSORED_PRODUCT_IMPRESSION,
          sku: browseObject.sku,
          campaignId: browseObject.sponsored_skus_data.campaign_id,
          msptbs: browseObject.sponsored_skus_data.msptbs,
          categoryId,
          // when a superbrowse page is paged or filtered, the browse objects won't have 'position', instead they have 'data_click_location' which has the format 'product1' for position 1
          position:
            browseObject.position != null
              ? browseObject.position
              : parseInt(
                  browseObject.data_click_location.replace(/[^\d]/g, ''),
                  10
                ),
          displayPrice:
            browseObject.calculated_unit_price || browseObject.list_price,
          placementId: browseObject.sponsored_skus_data.placement_id,
          slotId: browseObject.sponsored_skus_data.slot_id,
          referringTransactionId: this.config.transactionID,
          parentSku,
          isB2b: browseObject.sponsored_skus_data.is_b2b || false,
        }));
    }
    return [];
  }

  /**
   * extracts SponsoredProductImpressionEvents from browseObjects to be used with trackSandbox
   *
   * @param {Object} browseObject a browseObject
   * @param {Integer} categoryId the id of the browse category
   * @param {String} parentSku the SKU of the product whose page this sponsored product was shown on (if applicable)
   *
   * @return {Object} a SponsoredProductImpressionEvent
   */
  createSponsoredProductSandboxImpressionEvent(
    browseObject,
    categoryId,
    parentSku = null
  ) {
    if (browseObject.sku != null) {
      return {
        name: WSP_SANDBOX_IMPRESSION,
        sku: browseObject.sku,
        campaignId: browseObject.sponsored_skus_data.campaign_id,
        msptbs: browseObject.sponsored_skus_data.msptbs,
        categoryId,
        position: browseObject.position,
        displayPrice:
          browseObject.calculated_unit_price || browseObject.list_price,
        placementId: browseObject.sponsored_skus_data.placement_id,
        slotId: browseObject.sponsored_skus_data.slot_id,
        parentSku,
        referringTransactionId: this.config.transactionID,
        isB2b: browseObject.sponsored_skus_data.is_b2b || false,
      };
    }
    return null;
  }

  /**
   * extracts BillingPaymentOptionsEvents from paymentOptions to be used with sendBatchedEvents
   *
   * @param {Array} paymentOptions an array of paymentTypes
   * @param {String} source the source of the BillingPaymentOptionsEvent(s)
   * @param {Integer} defaultPaymentMethodId id of default payment method selected for a customer
   *
   * @return {Object} a BillingPaymentOptionsEvent object
   */
  // eslint-disable-next-line max-params
  createBillingPaymentOptionsEvent(
    paymentMethodIds,
    availablePaymentMethods,
    source,
    defaultPaymentMethodId = null
  ) {
    return {
      eventType: SCRIBE_EVENTS.BILLING_PAYMENT_OPTIONS,
      transactionId: this.config.transactionID,
      paymentMethodIds,
      availablePaymentMethods,
      source,
      defaultPaymentMethodId,
    };
  }

  /**
   * Track clicks on ads that are served externally on the site
   *
   * @param event
   */
  trackAdClick(event) {
    this.client.sendEvent(event);
  }

  /**
   * Track each first time an ad is displayed on screen
   *
   * @param event
   */
  trackAdImpression(event) {
    this.client.sendEvent(event, true);
  }

  /**
   * Track submissions and reasons for customer advocacy escalation and manager escalation
   *
   * @param event
   */
  trackCustomerEscalation(event) {
    this.client.sendEvent(event);
  }

  // sorsini moved from init page to client for easier use throughout decoupled apps
  recordEvent(name, data = {}) {
    if (!name || typeof name !== 'string') {
      throw new Error('recordEvent called without a string name');
    }
    const newName = name.substring(0, 32);
    const scribeData =
      typeof data === 'string' ? _unflattenRfCstmVars(data) : data;
    this.client.sendEvent({
      eventType: SCRIBE_EVENTS.WEB_ACTION,
      actionName: newName,
      actionData: scribeData,
    });
  }

  /**
   * Track all web actions
   *
   * @param {String} name Name of the web action
   * @param {Object} data Action specific data
   */
  trackWebAction(name, data = {}) {
    this.client.sendEvent({
      eventType: SCRIBE_EVENTS.WEB_ACTION,
      actionName: name,
      actionData: data,
    });
  }

  createSetLastRequestTxidCookie(transactionID) {
    return () => {
      this.config.cookieApi.setCookie('otx', transactionID, {
        expires: 90,
      });
    };
  }

  /**
   * Track user filtering activity on browse pages
   */
  trackAttributeAction(event) {
    event.eventType = SCRIBE_EVENTS.ATTRIBUTE_ACTION;
    this.client.sendEvent(event);
  }

  /**
   * Encodes a one-dimensional JS object (no nested objects) as a string Scribe can parse
   * {key0: value0, key1: value1} is encoded as {key0:value0,key1:value1}
   *
   * WARNING! Java does not provide built-in functionality to parse maps from GET request URL's
   * This function encodes a JS object as a string that Scribe's TranscriptionUtils.parseMap can decode
   * Only use this if absolutely necessary.
   *
   * @param {Object} objectField One dimensional object (no nested objects)
   *
   * @returns {String|null}
   */
  encodeScribeMap(objectField) {
    if (objectField === null) {
      return null;
    }
    let scribeMapString = '{';
    let entry = 0;
    for (const key in objectField) {
      if (entry > 0) {
        scribeMapString += ',';
      }
      if (objectField[key] == null) {
        scribeMapString += key.toString() + ':';
      } else {
        scribeMapString += key.toString() + ':' + objectField[key].toString();
      }
      entry++;
    }
    scribeMapString += '}';
    return scribeMapString;
  }

  /**
   * Encodes a one-dimensional JS array (no nested objects) as a string Scribe can parse
   * [value0, value1] is encoded as [value0,value1]
   *
   * WARNING! Java does not provide built-in functionality to parse lists from GET request URL's
   * This function encodes a JS object as a string that Scribe's TranscriptionUtils.parseList can decode
   * Only use this if absolutely necessary.
   *
   * @param {Array} listField One dimensional array (no nested arrays)
   *
   * @returns {String|null}
   */
  encodeScribeList(listField) {
    if (listField == null) {
      return null;
    }
    return '[' + listField.join() + ']';
  }

  /**
   * Track 3rd party actions
   *
   * @param {String} eventType  Type of event to send
   * @param {Object} data       Action specific data
   */
  trackThirdPartyAction(name, data = {}) {
    switch (name) {
      case 'malware_detected':
        this.client.sendEvent({
          eventType: SCRIBE_EVENTS.MALWARE_DETECTED,
          actionName: name,
          actionData: data,
        });
        // wf.malware_detected = true;
        break;
      default:
        return;
    }
  }

  /**
   * Track clicks on the order wizard tool
   *
   * @param {Object} event the data to send to Scribe
   */
  trackAdminOrderWizardClick(event) {
    event.eventType = SCRIBE_EVENTS.ADMIN_ORDER_WIZARD_CLICK;
    this.client.sendEvent(event);
  }

  /**
   * Track adminDpnCallerIdentityEvent from Caller Identity page
   *
   * @param {Object} event The event to send to scribe
   */
  trackAdminDpnCallerIdentity(event) {
    event.eventType = SCRIBE_EVENTS.ADMIN_DPN_CALLER_IDENTITY;
    this.client.sendEvent(event);
  }

  /**
   * Track the ClassSuggestionDisplayedEvent
   * @param event the event data to send to Scribe
   */
  trackClassSuggestionDisplayed(event) {
    event.eventType = SCRIBE_EVENTS.CLASS_SUGGESTION_DISPLAYED;
    this.client.sendEvent(event);
  }

  /**
   * Track contactUsEvent from Contact Us page
   *
   * @param {Object} event The event to send to scribe
   */
  trackContactUs(event) {
    event.eventType = SCRIBE_EVENTS.CONTACT_US;
    this.client.sendEvent(event);
  }

  /**
   * Track trackDesignerFeedback which indicates when a designer in a designer services program sends a feedback message.
   *
   * @param {Object} event The event to send to scribe
   */
  trackDesignerFeedback(event) {
    event.eventType = SCRIBE_EVENTS.DESIGNER_FEEDBACK;
    this.client.sendEvent(event);
  }

  /**
   * Track trackDesignerServicesCustomerFeedback which indicates when a customer in a designer services program sends a feedback message.
   *
   * @param {Object} event The event to send to scribe
   */
  trackDesignerServicesCustomerFeedback(event) {
    event.eventType = SCRIBE_EVENTS.DESIGNER_SERVICES_CUSTOMER_FEEDBACK;
    this.client.sendEvent(event);
  }

  /**
   * Track productContent events from media content loaded for a product on a PDP
   *
   * @param {Object} event The event to send to scribe
   */
  trackProductContent(event) {
    event.eventType = SCRIBE_EVENTS.PRODUCT_CONTENT;
    this.client.sendEvent(event);
  }

  /**
   * Track storefrontPushTokenRegistrationEvent for customers enabling browser notifications
   *
   * @param {Object} event The event to send to scribe
   */
  trackStorefrontPushTokenRegistration(event) {
    event.eventType = SCRIBE_EVENTS.STOREFRONT_PUSH_TOKEN_REGISTRATION;
    this.client.sendEvent(event);
  }

  /**
   * Track InViewEvent
   *
   * @param {Object} event The event to send to scribe
   */
  trackInViewEvent(event) {
    event.eventType = SCRIBE_EVENTS.IN_VIEW;
    // event.pageType = wf.get('constants.PAGE_TYPE');
    event.displaySkus = this.encodeScribeList(event.displaySkus);
    event.photoIds = this.encodeScribeList(event.photoIds);
    event.contentIds = this.encodeScribeList(event.contentIds);
    event.positions = this.encodeScribeList(event.positions);
    this.client.sendEvent(event);
  }

  /**
   * Track sandboxEvent for rapid prototyping of Scribe tracking
   *
   * @param {Object} event The event to send to scribe
   */
  trackSandbox(event) {
    event.eventType = SCRIBE_EVENTS.SANDBOX;
    this.client.sendEvent(event);
  }

  /**
   * Uses send beacon to log pixel fires in Pixel Activity Scribe event.
   *
   * @param {Object} event object that contains information of fired pixel
   *
   * @return bool true if data transfer queued successfully, false otherwise
   */
  trackPixelActivity = function(event) {
    return this.client.sendBatchedBeaconPost({
      ...event,
      eventType: SCRIBE_EVENTS.PIXEL_ACTIVITY,
    });
  };

  _setupHeartbeat() {
    if (this.heartbeat) {
      this.heartbeat.stop();
    }
    this.statsTracker.resetStats();
    this.heartbeat = new ScribeHeartbeat(this.client, this.statsTracker);
    const promise = this.heartbeat.start();
    return promise;
  }

  /**
   * AdBlockers are currently blocking t.wayfair
   * this will allow us to check and set a cookie where we can check for, on our backend, and mark
   * their tracking data as an ad blocking user
   *
   * @return void
   */
  determineBeaconAccessibilty() {
    /**
     * for each request that is a page load:
     * 1. remove the existing cookie - if any
     * 2. try to hit t.<storedomain>/b.php
     * 3. if success, do nothing - if failure, set a cookie with a value of 1 for one day
     * 4. downstream processes will track this cookie and tell us that client-side tracking will not be expected for
     *    these clients
     */
    this.config.cookieApi.removeCookie('ibb');

    const url =
      this.client.beaconUrl + '?' + $.param({_txid: this.config.transactionID});

    fetch(url, {
      credentials: 'include',
      method: 'GET',
    })
      // We do this instead of redirect: 'error' so we can throw a meaningful error.
      // Otherwise it's just ambiguous "failed to fetch"
      .catch(() => {
        // @TODO this logic differs a bit from resources ... need to verify
        this.config.cookieApi.setCookie('ibb', 1, {expires: SECONDS_PER_DAY});
      });
  }
}

export default Scribe;
